import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Megaphone } from 'lucide-react';

import { getMyAnnouncements, isUnopened } from './config';

const Wrap = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  color: ${({ theme }) => theme.colors.text.primary};
`;

// Kart tıklanır → detay (AKM POC §8: "açtı" = detayın açılması; liste okumak sayılmaz, açıklama 3 satırla kırpılır).
const Card = styled.div`
  background: #FFF;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  display: flex;
  cursor: pointer;
  transition: box-shadow 0.15s ease;
  &:hover { box-shadow: 0 4px 14px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.08); }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.primary.main}; outline-offset: 2px; }
  @media (max-width: 640px) { flex-direction: column; }
`;

const ImgBox = styled.div`
  width: 240px; flex-shrink: 0; background: ${({ theme }) => theme.colors.bgColor};
  display: flex; align-items: center; justify-content: center;
  @media (max-width: 640px) { width: 100%; height: 160px; }
`;

const Img = styled.img`
  width: 100%; height: 100%; object-fit: cover; max-height: 200px;
`;

const Body = styled.div`
  padding: 16px; flex: 1;
`;

const CardTitle = styled.h3`
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-weight: ${({ $unopened }) => ($unopened ? 800 : 600)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const UnopenedDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.primary.main};
`;

const UnopenedBadge = styled.span`
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary.main};
  background: ${({ theme }) => `${theme.colors.primary.main}1A`};
`;

const Description = styled.div`
  white-space: pre-wrap;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Meta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.light};
  display: flex; gap: 12px; flex-wrap: wrap;
`;

const DetailLink = styled.a`
  color: ${({ theme }) => theme.colors.primary.main};
  text-decoration: none; font-weight: 600;
  &:hover { text-decoration: underline; }
`;

const Empty = styled.div`
  padding: 40px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.light};
`;

export default function MyAnnouncementsPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMyAnnouncements()
      .then((r) => setItems(r.data?.data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Wrap>
      <Title>{t('announcement.myAnnouncements')}</Title>
      {loading ? null : items.length === 0 ? (
        <Empty>
          <Megaphone size={48} style={{ opacity: 0.4, marginBottom: 8 }} />
          <div>{t('announcement.noAnnouncements')}</div>
        </Empty>
      ) : (
        items.map((a) => {
          const unopened = isUnopened(a);
          const open = () => history.push(`/announcements/${a.id}`);
          return (
            <Card
              key={a.id}
              role="link"
              tabIndex={0}
              data-unopened={unopened ? 'true' : undefined}
              onClick={open}
              onKeyDown={(e) => { if (e.key === 'Enter') open(); }}
            >
              <ImgBox>
                {a.imageUrl ? <Img src={a.imageUrl} alt={a.title} /> : <Megaphone size={64} opacity={0.3} />}
              </ImgBox>
              <Body>
                <CardTitle $unopened={unopened}>
                  {unopened && <UnopenedDot aria-hidden="true" />}
                  {a.title}
                  {unopened && <UnopenedBadge>{t('announcement.unopened')}</UnopenedBadge>}
                </CardTitle>
                <Description>{a.description}</Description>
                {a.detailUrl && (
                  <DetailLink href={a.detailUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                    {t('announcement.openDetail')} →
                  </DetailLink>
                )}
                <Meta style={{ marginTop: 12 }}>
                  <span>{new Date(a.publishedAt).toLocaleDateString()}</span>
                  {a.expiryDate && <span>{t('announcement.until')}: {new Date(a.expiryDate).toLocaleDateString()}</span>}
                </Meta>
              </Body>
            </Card>
          );
        })
      )}
    </Wrap>
  );
}
